import { useDocumentTitle } from "usehooks-ts";
import {
  DataTableContextProvider,
  useDataTable,
} from "../DataTable/DataTableContext";
import { DataTable } from "../DataTable/DataTable";
import { useMutation } from "@tanstack/react-query";
import { executeQuery, REACT_APP_LEARNER_LINK } from "../../../utils";
import { useDeltaToastContext } from "../../../shared/contexts/ToasterContext";
import { useNavigate } from "react-router-dom";
import { LoginIcon } from "@heroicons/react/outline";
import { EmptyCell } from "../DataTable/EmptyCell";
import { TableHeaderSelectFilter } from "../DataTable/TableHeaderSelectFilter";
import { DMToggleSingleLabel } from "../../utils/functions";
import { useState } from "react";
import { LearnerStatsCards } from "./LearnerStatsCards";
import { CourseStatsModal } from "./CourseStatsModal";
import Button from "../../../student/components/generic/button";

export const AllLearnersPage: React.FC = () => {
  useDocumentTitle("All Learners Beta");
  const navigate = useNavigate();
  const toastContext = useDeltaToastContext();
  const [courseStatsModalOpen, setCourseStatsModalOpen] = useState(false);

  const { mutate: impersonate } = useMutation({
    mutationFn: (learnerId: string) => {
      return executeQuery({
        path: `/impersonate/staff/learner/${learnerId}`,
        method: "post",
      });
    },
    onSuccess: (response) => {
      localStorage.setItem("user", JSON.stringify(response.data.user));
      navigate(`${REACT_APP_LEARNER_LINK}/dashboard`, {
        state: { from: "/all-learners" },
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        "There was an error impersonating the learner";
      toastContext.addToast({
        title: "Error",
        message,
        status: "Error",
      });
    },
  });

  return (
    <div className="flex flex-col gap-8 p-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">All Learners (Beta)</h1>
        <Button onClick={() => setCourseStatsModalOpen(true)}>
          Course Statistics
        </Button>
      </header>
      <DataTableContextProvider
        source="/manager_new/parent-portal/learners-beta"
        defaults={{
          sortBy: [{ id: "subscriptionTrialStartedAt", desc: true }],
          pageSize: 100,
        }}
        sortable={[
          "first",
          "last",
          "email",
          "login_count",
          "last_action",
          "recentCourse",
          "pointsEarned",
          "pointsUsed",
          "sectionsCompleted",
          "unitsCompleted",
          "subscriptionTrialStartedAt",
          "statusAndDate.endDate",
          "last_login",
          "total_app_time",
          "interval",
          "cost",
          "parentEmail",
          "totalQuestionsAnswered",
          "totalQuestionsCorrect",
          "endDate",
          "trialLength",
        ]}
        filterable={[
          "first",
          "last",
          "email",
          "status",
          "recentCourse",
          "parentEmail",
        ]}
        filterOverrides={{
          status: ({ column }) => (
            <TableHeaderSelectFilter
              key={column.id}
              column={column}
              className="!min-w-44"
              choices={[
                "Trialist",
                "Subscriber",
                "Inactive",
                "Subscription Ending",
                "Trial Canceled",
                "Not Invited",
              ]}
            />
          ),
        }}
        globalFilter
        fetchOnLoad
        columnOverrides={{
          email: ({ row, value }) => {
            const {
              value: email,
              _id: learnerId,
              account_type,
            } = JSON.parse(value);
            return account_type === "learner" ? (
              <div className="flex items-center gap-2" key={learnerId}>
                <div>{email}</div>
                <LoginIcon
                  className="h-5 w-5 cursor-pointer text-dm-brand-blue-500"
                  onClick={() => impersonate(learnerId)}
                />
              </div>
            ) : (
              <EmptyCell />
            );
          },
          parentEmail: ({ row, value }) => {
            const { value: email, _id } = JSON.parse(value);
            return email ? (
              <div className="flex items-center gap-2" key={_id}>
                <div>{email}</div>
                <LoginIcon
                  className="h-5 w-5 cursor-pointer text-dm-brand-blue-500"
                  onClick={() => impersonate(_id)}
                />
              </div>
            ) : (
              <EmptyCell />
            );
          },
        }}
        getRowProps={(row) => {
          const isLearnerPlaceholder =
            (row.original.first as string) === "Pending";
          if (isLearnerPlaceholder) {
            return {
              className: "italic text-gray-500",
            };
          }
          return {};
        }}
        customButtons={[<AllLearnersToggles key="all-learners-toggle" />]}
      >
        <LearnerStatsCards />
        <DataTable />
      </DataTableContextProvider>
      <CourseStatsModal
        open={courseStatsModalOpen}
        onClose={() => setCourseStatsModalOpen(false)}
      />
    </div>
  );
};

const AllLearnersToggles: React.FC = () => {
  const [includeEmployees, setIncludeEmployees] = useState<boolean>(false);
  const [includeFree, setIncludeFree] = useState<boolean>(false);
  const { setExternalFilterBy } = useDataTable();

  return (
    <div className="flex gap-3">
      <DMToggleSingleLabel
        checked={includeEmployees}
        onChange={(checked: boolean) => {
          setIncludeEmployees(checked);
          setExternalFilterBy([
            {
              id: "includeEmployees",
              value: checked,
            },
            {
              id: "includeFree",
              value: includeFree,
            },
          ]);
        }}
        label="Include Employee Created Users"
        small
      />
      <DMToggleSingleLabel
        checked={includeFree}
        onChange={(checked: boolean) => {
          setIncludeFree(checked);
          setExternalFilterBy([
            {
              id: "includeFree",
              value: checked,
            },
            {
              id: "includeEmployees",
              value: includeEmployees,
            },
          ]);
        }}
        label="Include Free Accounts"
        small
      />
    </div>
  );
};
